import { Link } from '@tanstack/react-router';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import type { SearchPreviewState } from '#/components/search/searchPagination';
import { useDismissibleOverlay } from '#/hooks/useDismissibleOverlay';
import { useFocusTrap } from '#/hooks/useFocusTrap';
import { cn } from '#/utils/cn';

import {
  CategoryNavigationCta,
  CategoryNavigationItemsList,
} from './ItemLists';
import { MenuIcon, SearchAffordance } from './shared';
import type { CategoryNavigationItem, DesktopTabId } from './types';

function DesktopTabs({
  activeTab,
  onChange,
}: {
  activeTab: DesktopTabId;
  onChange: (tab: DesktopTabId) => void;
}) {
  const tabOrder: DesktopTabId[] = ['productTypes', 'sectors'];

  const focusTab = (tab: DesktopTabId) => {
    const tabElement = document.getElementById(
      tab === 'productTypes'
        ? 'category-navigation-tab-product-types'
        : 'category-navigation-tab-sectors',
    );

    tabElement?.focus();
  };

  const selectTab = (tab: DesktopTabId | undefined) => {
    if (!tab) {
      return;
    }

    onChange(tab);
    focusTab(tab);
  };

  const handleKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    tab: DesktopTabId,
  ) => {
    const currentIndex = tabOrder.indexOf(tab);

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab(tabOrder[(currentIndex + 1) % tabOrder.length]);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab(
        tabOrder[(currentIndex - 1 + tabOrder.length) % tabOrder.length],
      );
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      selectTab(tabOrder[0]);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      selectTab(tabOrder[tabOrder.length - 1]);
    }
  };

  return (
    <div className="px-20 pt-14 xl:px-28">
      <div
        role="tablist"
        aria-label="Category navigation sections"
        className="border-blue-20 flex items-end gap-20 border-b"
      >
        <button
          type="button"
          role="tab"
          id="category-navigation-tab-product-types"
          aria-selected={activeTab === 'productTypes'}
          aria-controls="category-navigation-panel-product-types"
          tabIndex={activeTab === 'productTypes' ? 0 : -1}
          onClick={() => onChange('productTypes')}
          onKeyDown={(event) => handleKeyDown(event, 'productTypes')}
          className={cn(
            'text-static-14 border-b-4 px-2 pt-10 pb-12 font-semibold transition-colors',
            activeTab === 'productTypes'
              ? 'text-fg-header border-blue-100'
              : 'text-slate-60 hover:text-fg-header border-transparent',
          )}
        >
          Investment Products
        </button>
        <button
          type="button"
          role="tab"
          id="category-navigation-tab-sectors"
          aria-selected={activeTab === 'sectors'}
          aria-controls="category-navigation-panel-sectors"
          tabIndex={activeTab === 'sectors' ? 0 : -1}
          onClick={() => onChange('sectors')}
          onKeyDown={(event) => handleKeyDown(event, 'sectors')}
          className={cn(
            'text-static-14 border-b-4 px-2 pt-10 pb-12 font-medium transition-colors',
            activeTab === 'sectors'
              ? 'text-fg-header border-blue-100'
              : 'text-slate-60 hover:text-fg-header border-transparent',
          )}
        >
          Sectors
        </button>
      </div>
    </div>
  );
}

interface DesktopNavigationProps {
  title: string;
  searchLabel: string;
  searchQuery?: string;
  previewSearchState?: SearchPreviewState;
  enableSearchPreview?: boolean;
  searchPrompt: string;
  browseEyebrow: string;
  browseHeading: string;
  browseDescription: string;
  productTypes: CategoryNavigationItem[];
  sectors: CategoryNavigationItem[];
  cta?: CategoryNavigationItem;
  activeHref?: string;
  activeTab: DesktopTabId;
  isPinned: boolean;
  isSearchPending?: boolean;
  pinReleaseOffsetRef: RefObject<number>;
  pinTransitionLockRef: RefObject<boolean>;
  overlayOpen: boolean;
  isInteractive: boolean;
  forceDesktopLayout: boolean;
  onTabChange: (tab: DesktopTabId) => void;
  onMenuToggle: () => void;
  onOverlayOpenChange: (open: boolean) => void;
  onSearchSubmit?: (query: string) => void;
  onSearchClick?: () => void;
}

const PIN_GUARD_TIMEOUT = 220;

export function DesktopNavigation({
  title,
  searchLabel,
  searchQuery,
  previewSearchState,
  enableSearchPreview,
  searchPrompt,
  browseEyebrow,
  browseHeading,
  browseDescription,
  productTypes,
  sectors,
  cta,
  activeHref,
  activeTab,
  isPinned,
  pinReleaseOffsetRef,
  pinTransitionLockRef,
  overlayOpen,
  isInteractive,
  forceDesktopLayout,
  onTabChange,
  onMenuToggle,
  onOverlayOpenChange,
  onSearchSubmit,
  onSearchClick,
  isSearchPending,
}: DesktopNavigationProps) {
  const shellRef = useRef<HTMLElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const previousPinnedRef = useRef<boolean | null>(null);
  const previousHeightRef = useRef<number | null>(null);
  const pinTransitionTimeoutRef = useRef<number | null>(null);
  const desktopHeading =
    activeTab === 'productTypes' ? browseHeading : 'Sector';
  const desktopDescription =
    activeTab === 'productTypes'
      ? browseDescription
      : 'Categories based on similar products or activities.';
  const desktopPanelId =
    activeTab === 'productTypes'
      ? 'category-navigation-panel-product-types'
      : 'category-navigation-panel-sectors';
  const desktopTabId =
    activeTab === 'productTypes'
      ? 'category-navigation-tab-product-types'
      : 'category-navigation-tab-sectors';
  const desktopOverlayStyle = {
    transform: overlayOpen ? 'translateY(0px)' : 'translateY(-8px)',
    willChange: 'transform, opacity',
  } as const;
  useFocusTrap({
    active: overlayOpen && isInteractive,
    containerRef: overlayRef,
    returnFocusRef: menuButtonRef,
    allowOutsideClick: true,
  });

  useDismissibleOverlay({
    active: overlayOpen && isInteractive,
    insideRefs: [overlayRef, menuButtonRef],
    onDismiss: () => {
      onOverlayOpenChange(false);
    },
  });

  useEffect(() => {
    return () => {
      if (pinTransitionTimeoutRef.current !== null) {
        window.clearTimeout(pinTransitionTimeoutRef.current);
      }
      pinTransitionLockRef.current = false;
    };
  }, [pinTransitionLockRef]);

  useLayoutEffect(() => {
    const shell = shellRef.current;

    if (!shell) {
      return;
    }

    const currentHeight = shell.getBoundingClientRect().height;
    const previousPinned = previousPinnedRef.current;
    const previousHeight = previousHeightRef.current;

    if (
      previousPinned !== null &&
      previousPinned !== isPinned &&
      previousHeight !== null
    ) {
      pinReleaseOffsetRef.current = Math.max(
        0,
        Math.ceil(previousHeight - currentHeight) + 1,
      );

      if (window.scrollY > 0) {
        pinTransitionLockRef.current = true;

        if (pinTransitionTimeoutRef.current !== null) {
          window.clearTimeout(pinTransitionTimeoutRef.current);
        }

        pinTransitionTimeoutRef.current = window.setTimeout(() => {
          pinTransitionLockRef.current = false;
          pinTransitionTimeoutRef.current = null;
        }, PIN_GUARD_TIMEOUT);
      }
    }

    previousPinnedRef.current = isPinned;
    previousHeightRef.current = currentHeight;
  }, [isPinned, pinReleaseOffsetRef, pinTransitionLockRef]);

  const overlay = (
    <div
      ref={overlayRef}
      id="category-navigation-desktop-overlay"
      aria-hidden={overlayOpen ? undefined : true}
      className={cn(
        'border-blue-20 rounded-b-12 absolute top-[calc(100%+1px)] right-0 left-0 z-0 overflow-hidden border border-t-0 shadow-none motion-reduce:transition-none',
        overlayOpen
          ? 'visible opacity-100 [transition:opacity_150ms_cubic-bezier(0.2,0,0,1),visibility_0s_linear_0s]'
          : 'pointer-events-none invisible opacity-0 [transition:opacity_150ms_cubic-bezier(0.2,0,0,1),visibility_0s_linear_150ms]',
      )}
    >
      <div
        style={desktopOverlayStyle}
        className="bg-gray-10 rounded-b-12 transition-[transform] duration-150 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none"
      >
        <DesktopTabs activeTab={activeTab} onChange={onTabChange} />
        <div className={cn('px-24 py-24', 'xl:px-32 xl:py-28')}>
          <div
            role="tabpanel"
            id={desktopPanelId}
            aria-labelledby={desktopTabId}
          >
            {activeTab === 'productTypes' ? (
              <div
                className={cn(
                  'grid gap-x-24 gap-y-20',
                  forceDesktopLayout
                    ? 'grid-cols-[14rem_minmax(0,1fr)]'
                    : 'md:grid-cols-[14rem_minmax(0,1fr)]',
                )}
              >
                <div className={cn(forceDesktopLayout ? 'pt-6' : 'md:pt-6')}>
                  <p className="text-fg-utility text-static-12 font-semibold tracking-[0.12em] uppercase">
                    {browseEyebrow}
                  </p>
                  <h2 className="text-fg-header mt-10 text-[24px] leading-normal font-normal whitespace-nowrap">
                    {desktopHeading}
                  </h2>
                  {desktopDescription ? (
                    <p className="text-slate-60 text-static-14 mt-10 leading-snug">
                      {desktopDescription}
                    </p>
                  ) : null}
                </div>
                <div className={cn(forceDesktopLayout ? 'pt-6' : 'md:pt-6')}>
                  <CategoryNavigationItemsList
                    items={productTypes}
                    activeHref={activeHref}
                    variant="desktopCards"
                    columnsClass="grid-cols-2 min-[68rem]:grid-cols-4"
                    extraItem={<CategoryNavigationCta cta={cta} />}
                  />
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  'grid gap-20',
                  forceDesktopLayout
                    ? 'grid-cols-[13rem_minmax(0,1fr)]'
                    : 'xl:grid-cols-[13rem_minmax(0,1fr)]',
                )}
              >
                <div
                  className={cn(
                    'max-w-[13rem]',
                    forceDesktopLayout ? 'pt-6' : 'xl:pt-6',
                  )}
                >
                  <p className="text-fg-utility text-static-12 font-semibold tracking-[0.12em] uppercase">
                    {browseEyebrow}
                  </p>
                  <h2 className="text-fg-header mt-10 text-[24px] leading-normal font-normal">
                    {desktopHeading}
                  </h2>
                  {desktopDescription ? (
                    <p className="text-slate-60 text-static-14 mt-10 leading-snug">
                      {desktopDescription}
                    </p>
                  ) : null}
                </div>
                <div className={cn(forceDesktopLayout ? 'pt-6' : 'xl:pt-6')}>
                  <CategoryNavigationItemsList
                    items={sectors}
                    activeHref={activeHref}
                    variant="desktopLinks"
                    columnsClass={
                      forceDesktopLayout
                        ? 'grid-cols-3 gap-x-16'
                        : 'lg:grid-cols-3 gap-x-16'
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <nav
      ref={shellRef}
      aria-label="Category navigation desktop"
      className={cn(
        'rounded-12 border-blue-20 bg-gray-10 relative isolate transition-[box-shadow,border-color,background-color] duration-200 ease-out motion-reduce:transition-none',
        isPinned ? 'border-b shadow-none' : 'border-y shadow-none',
      )}
      data-pinned={isPinned ? 'true' : 'false'}
    >
      <div
        className={cn(
          'bg-gray-10 relative z-10 rounded-t-[inherit]',
          overlayOpen ? 'rounded-b-none' : undefined,
        )}
      >
        <div
          aria-hidden="true"
          data-category-navigation-desktop-spacer="true"
          className={cn(
            'overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none',
            isPinned ? 'h-0' : 'h-20',
          )}
        />
        <div
          className={cn(
            'grid items-center py-20',
            // Mirror the body content grid's default (non-drawer) tracks so the
            // header aligns with the page columns, but keep it static: this
            // header must NOT collapse or hide its items when the definition
            // drawer opens, unlike the drawer-reactive `grid-cols-content`.
            '[grid-template-columns:[full-start]_var(--full)_[leftside-start]_minmax(0,clamp(0rem,17vw,18rem))_[leftside-end]_var(--gap)_[content-start]_var(--content-alt)_[content-end]_var(--gap)_[rightside-start]_minmax(0,clamp(0rem,17vw,18rem))_[rightside-end]_var(--full)_[full-end]]',
          )}
        >
          <div className="relative z-10 row-start-1 flex items-center gap-12 justify-self-start [grid-column:leftside-start/leftside-end]">
            <button
              ref={menuButtonRef}
              type="button"
              aria-label="Open site menu"
              aria-expanded={overlayOpen}
              aria-controls="category-navigation-desktop-overlay"
              onClick={onMenuToggle}
              className="text-fg-header flex shrink-0 items-center"
            >
              <MenuIcon className="size-22" />
            </button>
            <Link
              to="/"
              className="text-fg-header lg:text-static-14 xl:text-static-18 leading-tight font-semibold text-balance"
            >
              {title}
            </Link>
          </div>
          <div className="row-start-1 w-full justify-self-stretch [grid-column:content-start/rightside-end]">
            <SearchAffordance
              searchLabel={searchLabel}
              query={searchQuery}
              searchState={previewSearchState}
              enablePreview={enableSearchPreview}
              trackContext="search_bar"
              searchPrompt={searchPrompt}
              condensed={isPinned}
              onSubmitQuery={onSearchSubmit}
              onClick={onSearchClick}
              isPending={isSearchPending}
            />
          </div>
        </div>
        <div
          aria-hidden="true"
          data-category-navigation-desktop-spacer="true"
          className={cn(
            'overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none',
            isPinned ? 'h-0' : 'h-20',
          )}
        />
      </div>
      {overlay}
    </nav>
  );
}
